import React, { useState, useEffect } from 'react';
import { Button, Spinner, Alert, Label } from 'reactstrap';
import { useStripe, useElements, PaymentElement, Elements } from '@stripe/react-stripe-js';
import { billingApi } from '../../helpers/billing_api_helper';

interface PaymentMethodUpdateProps {
  stripePromise: any;
  onSuccess: () => void;
  onCancel: () => void;
}

// Payment method form that uses Stripe Elements
const PaymentMethodForm: React.FC<{
  clientSecret: string;
  onSuccess: () => void;
  onCancel: () => void;
}> = ({ clientSecret, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { error: stripeError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/billing`,
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        setError(stripeError.message || 'Failed to update payment method');
      } else if (setupIntent && setupIntent.status === 'succeeded') {
        // Setup successful! Now update the customer's default payment method
        try {
          await billingApi.updatePaymentMethod(setupIntent.id);
          onSuccess();
        } catch (updateError) {
          console.error('Error updating payment method on backend:', updateError);
          setError('Payment method was added but failed to set as default. Please try again.');
        }
      } else {
        setError('Payment method setup was not successful. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update payment method');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div>
      <div className="text-center mb-4">
        <h5>Update Payment Method</h5>
        <p className="text-muted">
          Enter your new payment method details below
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <Label>Payment Method</Label>
          <PaymentElement />
        </div>

        {error && (
          <Alert color="danger" className="mb-4">
            <i className="ri-error-warning-line me-2"></i>
            {error}
          </Alert>
        )}

        <div className="d-flex gap-2">
          <Button 
            type="submit" 
            color="primary" 
            disabled={!stripe || processing}
            className="flex-fill"
          >
            {processing ? (
              <>
                <Spinner size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              'Update Payment Method'
            )}
          </Button>
          
          <Button 
            type="button" 
            color="secondary" 
            onClick={onCancel}
            disabled={processing}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

// Main component for payment method update
const PaymentMethodUpdate: React.FC<PaymentMethodUpdateProps> = ({ stripePromise, onSuccess, onCancel }) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    createSetupIntent();
  }, []);

  const createSetupIntent = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await billingApi.createSetupIntent();
      setClientSecret(response.data.clientSecret);
    } catch (error: any) {
      console.error('Error creating setup intent:', error);
      setError('Failed to initialize payment method update. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = () => {
    onSuccess();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
        <Spinner color="primary" />
        <span className="ms-2">Initializing...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-4">
        <Alert color="danger">
          <i className="ri-error-warning-line me-2"></i>
          {error}
        </Alert>
        <div className="d-flex gap-2 justify-content-center">
          <Button color="primary" onClick={createSetupIntent}>
            Try Again
          </Button>
          <Button color="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {clientSecret && stripePromise && (
        <Elements 
          stripe={stripePromise} 
          options={{ 
            clientSecret,
            appearance: {
              theme: 'stripe'
            }
          }}
        >
          <PaymentMethodForm
            clientSecret={clientSecret}
            onSuccess={handleSuccess}
            onCancel={onCancel}
          />
        </Elements>
      )}
    </div>
  );
};

export default PaymentMethodUpdate;